import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLocaleContext } from '../context/LocaleContext';
import { getCanonicalUrl, getRobotsDirective } from '../utils/canonicalUrl';
import { normalizeRoute, getHreflangLinks } from '../utils/hreflang';
import { generateBreadcrumbList, breadcrumbsToJsonLd } from '../utils/breadcrumbs';

interface SEOHeadProps {
  title?: string;
  description?: string;
}

export const SEOHead: React.FC<SEOHeadProps> = ({ title, description }) => {
  const { locale } = useLocaleContext();
  const location = useLocation();

  useEffect(() => {
    const baseUrl = 'https://gpt-watermark-remover.com';
    const currentPath = location.pathname + location.search; // Include search params for detection

    // Get canonical URL using helper (automatically strips query params)
    const canonicalUrl = getCanonicalUrl(location.pathname);

    // Get hreflang URLs for all language variants using new centralized logic
    const normalized = normalizeRoute(location.pathname);
    const hreflangLinks = getHreflangLinks(normalized);

    // Get robots directive (for search pages and restricted pages)
    const robotsDirective = getRobotsDirective(currentPath);

    // Batch DOM reads first to avoid forced reflows
    const head = document.head;
    const existingElements = {
      metaDescription: document.querySelector('meta[name="description"]'),
      metaRobots: document.querySelector('meta[name="robots"]'),
      ogTitle: document.querySelector('meta[property="og:title"]'),
      ogDescription: document.querySelector('meta[property="og:description"]'),
      twitterTitle: document.querySelector('meta[name="twitter:title"]'),
      twitterDescription: document.querySelector('meta[name="twitter:description"]'),
      canonicalLink: document.querySelector('link[rel="canonical"]'),
      ogLocale: document.querySelector('meta[property="og:locale"]'),
      ogUrl: document.querySelector('meta[property="og:url"]'),
      existingHreflangs: document.querySelectorAll('link[hreflang]'),
      existingAlternates: document.querySelectorAll('meta[property="og:locale:alternate"]'),
    };

    // Page-specific SEO configurations with full localization
    // Type definition for page SEO config - all locales supported
    type LocalizedSEO = { title: string; description: string };
    type PageSEOConfig = {
      en: LocalizedSEO;
      de: LocalizedSEO;
      'pt-BR': LocalizedSEO;
      'ms-MY': LocalizedSEO;
      es: LocalizedSEO;
      fr: LocalizedSEO;
      ja: LocalizedSEO;
      id: LocalizedSEO;
    };

    const seoConfig: Record<string, Partial<PageSEOConfig>> = {
      '/remove-chatgpt-watermarks': {
        en: {
          title: 'Remove ChatGPT Watermarks – Free AI Text Cleaner',
          description: 'Fix ChatGPT code breaking linters and Git diffs. Remove zero-width characters causing formatting errors. Clean ChatGPT-generated code instantly. 100% browser-based.',
        },
        de: {
          title: 'ChatGPT-Wasserzeichen entfernen – Kostenloser KI-Reiniger',
          description: 'Beheben Sie ChatGPT-Code, der Linter und Git-Diffs bricht. Entfernen Sie Zero-Width-Zeichen, die Formatierungsfehler verursachen. Bereinigen Sie ChatGPT-generierten Code sofort.',
        },
        'pt-BR': {
          title: 'Remover Marcas d\'água ChatGPT – Limpador Gratuito',
          description: 'Corrija código ChatGPT quebrando linters e Git diffs. Remova caracteres de largura zero causando erros de formatação. Limpe código gerado pelo ChatGPT instantaneamente.',
        },
        'ms-MY': {
          title: 'Buang Tanda Air ChatGPT – Pembersih Teks Percuma',
          description: 'Betulkan kod ChatGPT yang merosakkan linters dan Git diffs. Buang aksara lebar sifar yang menyebabkan ralat pemformatan. Bersihkan kod yang dijana ChatGPT dengan segera.',
        },
        es: {
          title: 'Eliminar Marcas de Agua ChatGPT – Limpiador Gratuito',
          description: 'Arregla el código de ChatGPT que rompe linters y Git diffs. Elimina caracteres de ancho cero que causan errores de formato. Limpia código generado por ChatGPT al instante.',
        },
        fr: {
          title: 'Supprimer les Filigranes ChatGPT – Nettoyeur de Texte Gratuit',
          description: 'Corrigez le code ChatGPT qui casse les linters et Git diffs. Supprimez les caractères de largeur nulle causant des erreurs de formatage. Nettoyez le code généré par ChatGPT instantanément.',
        },
        ja: {
          title: 'ChatGPT透かしを削除 – 無料AIテキストクリーナー',
          description: 'リンターやGit diffを壊すChatGPTコードを修正。フォーマットエラーを引き起こすゼロ幅文字を削除。ChatGPT生成コードを即座にクリーンアップ。100%ブラウザベース。',
        },
        id: {
          title: 'Hapus Watermark ChatGPT – Pembersih Teks AI Gratis',
          description: 'Perbaiki kode ChatGPT yang merusak linter dan Git diff. Hapus karakter zero-width yang menyebabkan kesalahan format. Bersihkan kode ChatGPT secara instan. 100% berbasis browser.',
        },
      },
      '/remove-gemini-watermarks': {
        en: {
          title: 'Remove Google Gemini Watermarks – Free AI Text Cleaner',
          description: 'Remove invisible characters from Gemini text. Cleans zero-width spaces and joiners that break code and formatting.',
        },
        de: {
          title: 'Google Gemini Wasserzeichen entfernen – Kostenloser KI-Text-Reiniger',
          description: 'Entfernen Sie unsichtbare Zeichen aus Gemini-Text. Bereinigt Zero-Width-Spaces und Verbinder, die Code und Formatierung stören.',
        },
        'pt-BR': {
          title: 'Remover Marcas d\'Água do Google Gemini – Grátis',
          description: 'Remova caracteres invisíveis do texto do Gemini. Limpa espaços de largura zero e juntores que quebram código e formatação.',
        },
        'ms-MY': {
          title: 'Buang Tanda Air Google Gemini – Percuma',
          description: 'Buang aksara tidak kelihatan dari teks Gemini. Membersihkan ruang lebar-sifar dan penyambung yang merosakkan kod dan pemformatan.',
        },
        es: {
          title: 'Eliminar Marcas de Agua de Google Gemini – Gratis',
          description: 'Elimina caracteres invisibles del texto de Gemini. Limpia espacios de ancho cero y unidores que rompen código y formato.',
        },
        fr: {
          title: 'Supprimer les Filigranes Google Gemini – Gratuit',
          description: 'Supprimez les caractères invisibles du texte Gemini. Nettoie les espaces de largeur nulle et les liants qui cassent code et mise en forme.',
        },
        ja: {
          title: 'Google Gemini透かしを削除 – 無料AIテキストクリーナー',
          description: 'Geminiテキストから不可視文字を削除。コードや書式を壊すゼロ幅スペースや結合子をクリーンアップ。',
        },
        id: {
          title: 'Hapus Watermark Google Gemini – Gratis',
          description: 'Hapus karakter tak terlihat dari teks Gemini. Membersihkan spasi lebar nol dan penggabung yang merusak kode dan pemformatan.',
        },
      },
      '/remove-gemini-image-watermark': {
        en: {
          title: 'Remove Gemini Image Watermark – Free AI Photo Cleaner Tool',
          description: 'Remove the visible Gemini watermark from Google Gemini AI-generated images. Detect the visible sparkle mark, strip EXIF metadata, and download clean photos. 100% private – works in your browser.',
        },
        de: {
          title: 'Gemini Bild-Wasserzeichen entfernen – Kostenloses KI-Foto-Tool',
          description: 'Entfernen Sie das sichtbare Gemini-Wasserzeichen aus Google Gemini KI-Bildern. Erkennen Sie das sichtbare Symbol, entfernen Sie EXIF-Metadaten und laden Sie saubere Fotos herunter. 100% privat – läuft im Browser.',
        },
        'pt-BR': {
          title: 'Remover Marca d\'Água de Imagem Gemini – Ferramenta Gratuita',
          description: 'Remova a marca d\'água visível do Google Gemini de imagens geradas por IA. Detecte a marca visível, remova metadados EXIF e baixe fotos limpas. 100% privado – funciona no seu navegador.',
        },
        'ms-MY': {
          title: 'Buang Tanda Air Imej Gemini – Alat Pembersih Foto AI Percuma',
          description: 'Buang tanda air Gemini yang boleh dilihat dari imej AI Google Gemini. Kesan tanda yang boleh dilihat, buang metadata EXIF, dan muat turun foto bersih. 100% peribadi – berfungsi dalam pelayar anda.',
        },
        es: {
          title: 'Eliminar Marca de Agua de Imagen Gemini – Herramienta Gratuita',
          description: 'Elimina la marca de agua visible de Google Gemini de imágenes generadas por IA. Detecta la marca visible, elimina metadatos EXIF y descarga fotos limpias. 100% privado – funciona en tu navegador.',
        },
        fr: {
          title: 'Supprimer le Filigrane Image Gemini – Outil Photo IA Gratuit',
          description: 'Supprimez le filigrane visible de Google Gemini des images générées par IA. Détectez le filigrane visible, supprimez les métadonnées EXIF et téléchargez des photos propres. 100% privé – fonctionne dans votre navigateur.',
        },
        ja: {
          title: 'Gemini画像透かしを削除 – 無料AIフォトクリーナー',
          description: 'Google Gemini AI生成画像から可視の透かしを削除する無料ツール。可視のマークを検出し、EXIFメタデータを削除してクリーンな写真をダウンロード。100%プライベート – ブラウザで動作。',
        },
        id: {
          title: 'Hapus Watermark Gambar Gemini – Alat Pembersih Foto AI Gratis',
          description: 'Hapus watermark Gemini yang terlihat dari gambar yang dihasilkan AI Google Gemini. Deteksi tanda yang terlihat, hapus metadata EXIF, dan unduh foto bersih. 100% privat – berfungsi di browser Anda.',
        },
      },
      '/remove-claude-watermarks': {
        en: {
          title: 'Remove Claude Watermarks - Clean Invisible Unicode from Claude Text',
          description: 'Remove invisible Unicode characters from Claude output. Clean zero-width spaces and joiners that break code and formatting. Free and private.',
        },
        de: {
          title: 'Claude-Wasserzeichen entfernen - Unsichtbare Unicode-Zeichen bereinigen',
          description: 'Entfernen Sie unsichtbare Unicode-Zeichen aus Claude-Ausgaben. Bereinigen Sie Zero-Width-Spaces und Verbinder, die Code und Formatierung stören. Kostenlos und privat.',
        },
        'pt-BR': {
          title: 'Remover Marcas d\'água do Claude - Limpe Unicode Invisível do Texto Claude',
          description: 'Remova caracteres Unicode invisíveis da saída do Claude. Limpe espaços de largura zero e juntores que quebram código e formatação. Grátis e privado.',
        },
        'ms-MY': {
          title: 'Buang Tanda Air Claude - Bersihkan Unicode Tidak Kelihatan',
          description: 'Buang aksara Unicode tidak kelihatan daripada output Claude. Bersihkan ruang lebar-sifar dan penyambung yang merosakkan kod dan pemformatan. Percuma dan peribadi.',
        },
        es: {
          title: 'Eliminar Marcas de Agua de Claude - Limpia Unicode Invisible',
          description: 'Elimina caracteres Unicode invisibles de la salida de Claude. Limpia espacios de ancho cero y unidores que rompen código y formato. Gratis y privado.',
        },
        fr: {
          title: 'Supprimer les Filigranes Claude - Nettoyez l\'Unicode Invisible',
          description: 'Supprimez les caractères Unicode invisibles de la sortie Claude. Nettoyez les espaces de largeur nulle et les liants qui cassent code et mise en forme. Gratuit et privé.',
        },
        ja: {
          title: 'Claude透かしを削除 - 不可視Unicode文字をクリーンアップ',
          description: 'Claudeの出力から不可視のUnicode文字を削除。コードや書式を壊すゼロ幅スペースや結合子をクリーンアップ。無料でプライベート。',
        },
        id: {
          title: 'Hapus Watermark Claude - Bersihkan Unicode Tak Terlihat',
          description: 'Hapus karakter Unicode tak terlihat dari output Claude. Bersihkan spasi lebar nol dan penggabung yang merusak kode dan pemformatan. Gratis dan privat.',
        },
      },
      '/ai-watermark-remover-students': {
        en: {
          title: 'AI Watermark Remover for Students - Clean Essays & Assignments Ethically',
          description: 'Students: Learn to use AI ethically while writing essays and assignments. Remove technical watermarks from AI-assisted homework. Free guide on academic integrity, proper AI citation, and responsible ChatGPT/Gemini use.',
        },
        de: {
          title: 'KI-Wasserzeichen-Entferner für Studierende - Essays & Hausarbeiten ethisch bereinigen',
          description: 'Studierende: Lernen Sie, KI ethisch beim Schreiben von Essays und Hausarbeiten zu nutzen. Entfernen Sie technische Wasserzeichen aus KI-unterstützten Hausaufgaben. Kostenloser Leitfaden zu akademischer Integrität und verantwortungsvollem KI-Einsatz.',
        },
        'pt-BR': {
          title: 'Removedor de Marca d\'água IA para Estudantes - Limpe Redações Eticamente',
          description: 'Estudantes: Aprenda a usar IA eticamente ao escrever redações e trabalhos. Remova marcas d\'água técnicas de trabalhos assistidos por IA. Guia gratuito sobre integridade acadêmica e uso responsável de ChatGPT/Gemini.',
        },
        'ms-MY': {
          title: 'Penyingkir Tanda Air AI untuk Pelajar - Bersihkan Esei & Tugasan Secara Beretika',
          description: 'Pelajar: Belajar menggunakan AI secara beretika semasa menulis esei dan tugasan. Buang tanda air teknikal dari kerja rumah yang dibantu AI. Panduan percuma tentang integriti akademik dan penggunaan ChatGPT/Gemini yang bertanggungjawab.',
        },
        es: {
          title: 'Eliminador de Marcas de Agua IA para Estudiantes - Limpia Ensayos Éticamente',
          description: 'Estudiantes: Aprende a usar IA éticamente al escribir ensayos y tareas. Elimina marcas de agua técnicas de trabajos asistidos por IA. Guía gratuita sobre integridad académica y uso responsable de ChatGPT/Gemini.',
        },
        fr: {
          title: 'Suppresseur de Filigrane IA pour Étudiants - Nettoyez vos Essais Éthiquement',
          description: 'Étudiants : Apprenez à utiliser l\'IA de manière éthique pour rédiger vos essais et devoirs. Supprimez les filigranes techniques des travaux assistés par IA. Guide gratuit sur l\'intégrité académique et l\'utilisation responsable de ChatGPT/Gemini.',
        },
        ja: {
          title: '学生向けAI透かし削除ツール - エッセイと課題を倫理的にクリーンアップ',
          description: '学生の皆さん：エッセイや課題を書く際にAIを倫理的に使用する方法を学びましょう。AI支援の宿題から技術的な透かしを削除。学術的誠実性、適切なAI引用、責任あるChatGPT/Gemini使用の無料ガイド。',
        },
        id: {
          title: 'Penghapus Watermark AI untuk Pelajar - Bersihkan Esai & Tugas Secara Etis',
          description: 'Pelajar: Pelajari cara menggunakan AI secara etis saat menulis esai dan tugas. Hapus watermark teknis dari pekerjaan rumah yang dibantu AI. Panduan gratis tentang integritas akademik dan penggunaan ChatGPT/Gemini yang bertanggung jawab.',
        },
      },
      '/detect-ai-watermarks': {
        en: {
          title: 'How to Detect AI Watermarks in Text - Free Detection Tool',
          description: 'Learn how to detect invisible Unicode characters in AI-generated text. Identify invisible zero-width Unicode characters that can break code and formatting. Free instant detection tool.',
        },
        de: {
          title: 'So erkennen Sie KI-Wasserzeichen in Text - Kostenloses Erkennungstool',
          description: 'Erfahren Sie, wie Sie unsichtbare Unicode-Zeichen in KI-generiertem Text erkennen. Identifizieren Sie unsichtbare Zero-Width-Unicode-Zeichen, die Code und Formatierung stören können.',
        },
        'pt-BR': {
          title: 'Como Detectar Marcas d\'Água de IA em Texto - Ferramenta Gratuita',
          description: 'Aprenda como detectar caracteres Unicode invisíveis em texto gerado por IA. Identifique caracteres Unicode de largura zero invisíveis que podem quebrar código e formatação.',
        },
        'ms-MY': {
          title: 'Cara Mengesan Tanda Air AI dalam Teks - Alat Pengesanan Percuma',
          description: 'Ketahui cara mengesan aksara Unicode tidak kelihatan dalam teks yang dijana AI. Kenal pasti aksara Unicode lebar sifar tidak kelihatan yang boleh merosakkan kod dan pemformatan.',
        },
        es: {
          title: 'Cómo Detectar Marcas de Agua de IA en Texto - Herramienta Gratuita',
          description: 'Aprende cómo detectar caracteres Unicode invisibles en texto generado por IA. Identifica caracteres Unicode de ancho cero invisibles que pueden romper código y formato.',
        },
        fr: {
          title: 'Comment Détecter les Filigranes IA dans le Texte - Outil de Détection Gratuit',
          description: 'Apprenez à détecter les caractères Unicode invisibles dans le texte généré par IA. Identifiez les caractères Unicode invisibles de largeur nulle qui peuvent casser le code et le formatage. Outil de détection instantanée gratuit.',
        },
        ja: {
          title: 'テキスト内のAI透かしを検出する方法 - 無料検出ツール',
          description: 'AI生成テキスト内の不可視Unicode文字を検出する方法を学びましょう。コードや書式を壊す可能性のある不可視のゼロ幅Unicode文字を特定。無料の即時検出ツール。',
        },
        id: {
          title: 'Cara Mendeteksi Watermark AI dalam Teks - Alat Deteksi Gratis',
          description: 'Pelajari cara mendeteksi karakter Unicode tak terlihat dalam teks yang dihasilkan AI. Identifikasi karakter Unicode zero-width tak terlihat yang dapat merusak kode dan pemformatan. Alat deteksi instan gratis.',
        },
      },
      '/api': {
        en: {
          title: 'Watermark Remover API - Remove AI Watermarks Programmatically',
          description: 'REST API to remove invisible AI watermarks from text. Detect and remove invisible Unicode characters that appear in AI text. 1,000 free API calls included.',
        },
        de: {
          title: 'Watermark Remover API - Remove AI Watermarks Programmatically',
          description: 'REST API to remove invisible AI watermarks from text. Detect and remove invisible Unicode characters that appear in AI text. 1,000 free API calls included.',
        },
        'pt-BR': {
          title: 'Watermark Remover API - Remove AI Watermarks Programmatically',
          description: 'REST API to remove invisible AI watermarks from text. Detect and remove invisible Unicode characters that appear in AI text. 1,000 free API calls included.',
        },
        'ms-MY': {
          title: 'Watermark Remover API - Remove AI Watermarks Programmatically',
          description: 'REST API to remove invisible AI watermarks from text. Detect and remove invisible Unicode characters that appear in AI text. 1,000 free API calls included.',
        },
        es: {
          title: 'Watermark Remover API - Remove AI Watermarks Programmatically',
          description: 'REST API to remove invisible AI watermarks from text. Detect and remove invisible Unicode characters that appear in AI text. 1,000 free API calls included.',
        },
        fr: {
          title: 'Watermark Remover API - Remove AI Watermarks Programmatically',
          description: 'REST API to remove invisible AI watermarks from text. Detect and remove invisible Unicode characters that appear in AI text. 1,000 free API calls included.',
        },
        ja: {
          title: 'Watermark Remover API - Remove AI Watermarks Programmatically',
          description: 'REST API to remove invisible AI watermarks from text. Detect and remove invisible Unicode characters that appear in AI text. 1,000 free API calls included.',
        },
        id: {
          title: 'Watermark Remover API - Remove AI Watermarks Programmatically',
          description: 'REST API to remove invisible AI watermarks from text. Detect and remove invisible Unicode characters that appear in AI text. 1,000 free API calls included.',
        },
      },
      '/detect': {
        en: {
          title: 'AI Watermark Detection – Scan Text for Hidden Characters',
          description: 'Detect invisible Unicode characters in AI-generated text. Scan for zero-width characters and other hidden Unicode markers. Free instant analysis.',
        },
        de: {
          title: 'KI-Wasserzeichen-Erkennung – Text auf versteckte Zeichen scannen',
          description: 'Erkennen Sie unsichtbare Unicode-Zeichen in KI-generiertem Text. Scannen Sie nach Zero-Width-Zeichen und anderen versteckten Unicode-Markierungen. Kostenlose sofortige Analyse.',
        },
        'pt-BR': {
          title: 'Detecção de Marca d\'Água IA – Escanear Texto por Caracteres Ocultos',
          description: 'Detecte caracteres Unicode invisíveis em texto gerado por IA. Escaneie caracteres de largura zero e outros marcadores Unicode ocultos. Análise instantânea gratuita.',
        },
        'ms-MY': {
          title: 'Pengesanan Tanda Air AI – Imbas Teks untuk Aksara Tersembunyi',
          description: 'Kesan aksara Unicode tidak kelihatan dalam teks yang dijana AI. Imbas aksara lebar sifar dan penanda Unicode tersembunyi lain. Analisis segera percuma.',
        },
        es: {
          title: 'Detección de Marcas de Agua IA – Escanear Texto por Caracteres Ocultos',
          description: 'Detecta caracteres Unicode invisibles en texto generado por IA. Escanea caracteres de ancho cero y otros marcadores Unicode ocultos. Análisis instantáneo gratuito.',
        },
        fr: {
          title: 'Détection de Filigrane IA – Scanner le Texte pour Caractères Cachés',
          description: 'Détectez les caractères Unicode invisibles dans le texte généré par IA. Scannez les caractères de largeur nulle et autres marqueurs Unicode cachés. Analyse instantanée gratuite.',
        },
        ja: {
          title: 'AI透かし検出 – テキストの隠し文字をスキャン',
          description: 'AI生成テキスト内の不可視Unicode文字を検出。ゼロ幅文字やその他の隠れたUnicodeマーカーをスキャン。無料で即座に分析。',
        },
        id: {
          title: 'Deteksi Watermark AI – Pindai Teks untuk Karakter Tersembunyi',
          description: 'Deteksi karakter Unicode tak terlihat dalam teks yang dihasilkan AI. Pindai karakter zero-width dan penanda Unicode tersembunyi lainnya. Analisis instan gratis.',
        },
      },
      '/pricing': {
        en: {
          title: 'Pricing Plans – GPT Watermark Remover',
          description: 'Compare GPT Watermark Remover plans: Free tier with 500 chars, Premium Monthly at $9.79, or Lifetime at $27.36. Detect and remove invisible zero-width characters from AI-generated text.',
        },
        de: {
          title: 'Preise – GPT Wasserzeichen Entferner',
          description: 'Vergleichen Sie GPT Wasserzeichen Entferner Pläne: Kostenlos mit 500 Zeichen, Premium Monatlich für 9,79€, oder Lifetime für 27,36€. Erkennen und entfernen Sie unsichtbare Zero-Width-Zeichen aus KI-generiertem Text.',
        },
        'pt-BR': {
          title: 'Preços – GPT Removedor de Marca d\'Água',
          description: 'Compare os planos do GPT Removedor de Marca d\'Água: Grátis com 500 caracteres, Premium Mensal por $9,79, ou Vitalício por $27,36. Detecte e remova caracteres invisíveis de largura zero de texto gerado por IA.',
        },
        'ms-MY': {
          title: 'Harga – GPT Penyingkir Tanda Air',
          description: 'Bandingkan pelan GPT Penyingkir Tanda Air: Percuma dengan 500 aksara, Premium Bulanan pada $9.79, atau Seumur Hidup pada $27.36. Kesan dan buang aksara lebar sifar tidak kelihatan daripada teks yang dijana AI.',
        },
        es: {
          title: 'Precios – GPT Eliminador de Marcas de Agua',
          description: 'Compara los planes de GPT Eliminador de Marcas de Agua: Gratis con 500 caracteres, Premium Mensual a $9.79, o Vitalicio a $27.36. Detecta y elimina caracteres invisibles de ancho cero de texto generado por IA.',
        },
        fr: {
          title: 'Tarifs – GPT Suppresseur de Filigrane',
          description: 'Comparez les plans GPT Suppresseur de Filigrane: Gratuit avec 500 caractères, Premium Mensuel à 9,79€, ou À Vie à 27,36€. Détectez et supprimez les caractères invisibles de largeur nulle du texte généré par IA.',
        },
        ja: {
          title: '料金プラン – GPT透かし除去ツール',
          description: 'GPT透かし除去ツールのプランを比較：無料プラン500文字、プレミアム月額$9.79、または買い切り$27.36。AI生成テキストから不可視のゼロ幅文字を検出・削除。',
        },
        id: {
          title: 'Harga – GPT Penghapus Watermark',
          description: 'Bandingkan paket GPT Penghapus Watermark: Gratis dengan 500 karakter, Premium Bulanan $9.79, atau Seumur Hidup $27.36. Deteksi dan hapus karakter zero-width tak terlihat dari teks yang dihasilkan AI.',
        },
      },
      '/faq': {
        en: {
          title: 'FAQ – AI Watermark Removal Questions Answered',
          description: 'Answers to common questions about invisible Unicode characters in AI-generated text, detection methods, privacy, and how GPT Watermark Remover cleans your text. Get help now.',
        },
        de: {
          title: 'FAQ – Häufige Fragen zur KI-Wasserzeichen-Entfernung',
          description: 'Antworten auf häufige Fragen zu unsichtbaren Unicode-Zeichen in KI-generiertem Text, Erkennungsmethoden, Datenschutz und wie GPT Wasserzeichen Entferner Ihren Text bereinigt. Jetzt Hilfe erhalten.',
        },
        'pt-BR': {
          title: 'FAQ – Perguntas sobre Remoção de Marca d\'Água IA',
          description: 'Respostas às perguntas comuns sobre caracteres Unicode invisíveis em texto gerado por IA, métodos de detecção, privacidade e como o GPT Removedor de Marca d\'Água limpa seu texto. Obtenha ajuda agora.',
        },
        'ms-MY': {
          title: 'FAQ – Soalan Lazim Penyingkiran Tanda Air AI',
          description: 'Jawapan kepada soalan lazim tentang aksara Unicode tidak kelihatan dalam teks yang dijana AI, kaedah pengesanan, privasi dan cara GPT Penyingkir Tanda Air membersihkan teks anda. Dapatkan bantuan sekarang.',
        },
        es: {
          title: 'FAQ – Preguntas sobre Eliminación de Marcas de Agua IA',
          description: 'Respuestas a preguntas comunes sobre caracteres Unicode invisibles en texto generado por IA, métodos de detección, privacidad y cómo GPT Eliminador de Marcas de Agua limpia tu texto. Obtén ayuda ahora.',
        },
        fr: {
          title: 'FAQ – Questions sur la Suppression de Filigrane IA',
          description: 'Réponses aux questions fréquentes sur les caractères Unicode invisibles dans le texte généré par IA, méthodes de détection, confidentialité et comment GPT Suppresseur de Filigrane nettoie votre texte. Obtenez de l\'aide.',
        },
        ja: {
          title: 'FAQ – AI透かし削除に関するよくある質問',
          description: 'AI生成テキスト内の不可視Unicode文字、検出方法、プライバシー、GPT透かし除去ツールがテキストをどのようにクリーンにするかについてのよくある質問への回答。今すぐヘルプを取得。',
        },
        id: {
          title: 'FAQ – Pertanyaan tentang Penghapusan Watermark AI',
          description: 'Jawaban atas pertanyaan umum tentang karakter Unicode tak terlihat dalam teks yang dihasilkan AI, metode deteksi, privasi, dan cara GPT Penghapus Watermark membersihkan teks Anda. Dapatkan bantuan sekarang.',
        },
      },
      '/knowledge': {
        en: {
          title: 'Knowledge Base – AI Watermark Technical Guides',
          description: 'Technical deep-dives into AI watermarking: token distribution, detection vs. removal, why AI detectors fail, and expert resources for understanding invisible text markers.',
        },
        de: {
          title: 'Wissensbasis – Technische Leitfäden zu KI-Wasserzeichen',
          description: 'Technische Vertiefungen in KI-Wasserzeichen: Token-Verteilung, Erkennung vs. Entfernung, warum KI-Detektoren versagen, und Expertenressourcen zum Verständnis unsichtbarer Textmarkierungen.',
        },
        'pt-BR': {
          title: 'Base de Conhecimento – Guias Técnicos de Marca d\'Água IA',
          description: 'Análises técnicas aprofundadas sobre marcas d\'água IA: distribuição de tokens, detecção vs. remoção, por que detectores de IA falham, e recursos especializados para entender marcadores de texto invisíveis.',
        },
        'ms-MY': {
          title: 'Pangkalan Pengetahuan – Panduan Teknikal Tanda Air AI',
          description: 'Penerangan teknikal mendalam tentang tanda air AI: pengagihan token, pengesanan vs. penyingkiran, mengapa pengesan AI gagal, dan sumber pakar untuk memahami penanda teks tidak kelihatan.',
        },
        es: {
          title: 'Base de Conocimientos – Guías Técnicas de Marcas de Agua IA',
          description: 'Análisis técnicos profundos sobre marcas de agua IA: distribución de tokens, detección vs. eliminación, por qué los detectores de IA fallan, y recursos expertos para entender marcadores de texto invisibles.',
        },
        fr: {
          title: 'Base de Connaissances – Guides Techniques de Filigrane IA',
          description: 'Analyses techniques approfondies sur les filigranes IA: distribution de tokens, détection vs. suppression, pourquoi les détecteurs IA échouent, et ressources expertes pour comprendre les marqueurs de texte invisibles.',
        },
        ja: {
          title: 'ナレッジベース – AI透かし技術ガイド',
          description: 'AI透かしの技術的詳細解説：トークン分布、検出vs削除、AI検出器が失敗する理由、見えないテキストマーカーを理解するための専門家リソース。',
        },
        id: {
          title: 'Basis Pengetahuan – Panduan Teknis Watermark AI',
          description: 'Penjelasan teknis mendalam tentang watermark AI: distribusi token, deteksi vs. penghapusan, mengapa detektor AI gagal, dan sumber daya ahli untuk memahami penanda teks tak terlihat.',
        },
      },
      '/': {
        en: {
          title: 'ChatGPT Watermark Remover – Clean AI Text Free',
          description: 'Remove ChatGPT & AI watermarks from text and documents. Detect and remove invisible zero-width characters from Word, Pages & text. Free online tool – no data stored.',
        },
        de: {
          title: 'ChatGPT Wasserzeichen Entfernen – Kostenlos',
          description: 'Entfernen Sie ChatGPT & KI-Wasserzeichen aus Text und Dokumenten. Erkennen und entfernen Sie unsichtbare Zeichen ohne Breite aus Word, Pages & Text. Kostenloses Online-Tool – keine Datenspeicherung.',
        },
        'pt-BR': {
          title: 'Remover Marca d\'água ChatGPT – Grátis',
          description: 'Remova marcas d\'água de ChatGPT & IA de textos e documentos. Detecte e remova caracteres invisíveis de largura zero de Word, Pages & texto. Ferramenta online gratuita – sem armazenamento de dados.',
        },
        'ms-MY': {
          title: 'Buang Tanda Air ChatGPT – Percuma',
          description: 'Buang tanda air ChatGPT & AI dari teks dan dokumen. Kesan dan buang aksara tidak kelihatan lebar sifar dari Word, Pages & teks. Alat dalam talian percuma – tiada data disimpan.',
        },
        es: {
          title: 'Eliminar Marcas de Agua ChatGPT – Gratis',
          description: 'Elimina marcas de agua de ChatGPT e IA de texto y documentos. Detecta y elimina caracteres invisibles de ancho cero de Word, Pages y texto. Herramienta online gratuita – sin almacenamiento de datos.',
        },
        fr: {
          title: 'Suppresseur de Filigrane ChatGPT – Gratuit',
          description: 'Supprimez les filigranes ChatGPT et IA des textes et documents. Détectez et supprimez les caractères invisibles de largeur nulle dans Word, Pages et le texte. Outil en ligne gratuit – aucune donnée stockée.',
        },
        ja: {
          title: 'ChatGPT透かし削除ツール – 無料でAIテキストをクリーンに',
          description: 'ChatGPTとAIの透かしをテキストやドキュメントから削除します。Word、Pages、テキスト内の目に見えないゼロ幅文字を検出して削除します。無料のオンラインツール – データは保存されません。',
        },
        id: {
          title: 'Penghapus Watermark ChatGPT – Bersihkan Teks AI Gratis',
          description: 'Hapus watermark ChatGPT & AI dari teks dan dokumen. Deteksi dan hapus karakter tak terlihat lebar-nol dari Word, Pages & teks. Alat online gratis – tidak ada data yang disimpan.',
        },
      },
    };

    // Resolve the current path to its English canonical route for config lookup.
    // This handles both the locale prefix AND translated slugs (e.g.
    // /de/gemini-bild-wasserzeichen-entfernen -> /remove-gemini-image-watermark),
    // so localized routes hit their own seoConfig block instead of falling through.
    const pageConfig = seoConfig[normalized]?.[locale];

    // Use localized fallbacks based on current locale for homepage
    const localizedFallbacks: Record<string, LocalizedSEO> = {
      en: {
        title: 'ChatGPT Watermark Remover – Clean AI Text Free',
        description: 'Remove ChatGPT & AI watermarks from text and documents. Detect and remove invisible zero-width characters from Word, Pages & text. Free online tool – no data stored.',
      },
      de: {
        title: 'ChatGPT Wasserzeichen Entfernen – Kostenlos',
        description: 'Entfernen Sie ChatGPT & KI-Wasserzeichen aus Text und Dokumenten. Erkennen und entfernen Sie unsichtbare Zeichen ohne Breite aus Word, Pages & Text. Kostenloses Online-Tool – keine Datenspeicherung.',
      },
      'pt-BR': {
        title: 'Remover Marca d\'água ChatGPT – Grátis',
        description: 'Remova marcas d\'água de ChatGPT & IA de textos e documentos. Detecte e remova caracteres invisíveis de largura zero de Word, Pages & texto. Ferramenta online gratuita – sem armazenamento de dados.',
      },
      'ms-MY': {
        title: 'Buang Tanda Air ChatGPT – Percuma',
        description: 'Buang tanda air ChatGPT & AI dari teks dan dokumen. Kesan dan buang aksara tidak kelihatan lebar sifar dari Word, Pages & teks. Alat dalam talian percuma – tiada data disimpan.',
      },
      es: {
        title: 'Eliminar Marcas de Agua ChatGPT – Gratis',
        description: 'Elimina marcas de agua de ChatGPT e IA de texto y documentos. Detecta y elimina caracteres invisibles de ancho cero de Word, Pages y texto. Herramienta online gratuita – sin almacenamiento de datos.',
      },
      fr: {
        title: 'Suppresseur de Filigrane ChatGPT – Gratuit',
        description: 'Supprimez les filigranes ChatGPT et IA des textes et documents. Détectez et supprimez les caractères invisibles de largeur nulle dans Word, Pages et le texte. Outil en ligne gratuit – aucune donnée stockée.',
      },
      ja: {
        title: 'ChatGPT透かし削除ツール – 無料でAIテキストをクリーンに',
        description: 'ChatGPTとAIの透かしをテキストやドキュメントから削除します。Word、Pages、テキスト内の目に見えないゼロ幅文字を検出して削除します。無料のオンラインツール – データは保存されません。',
      },
      id: {
        title: 'Penghapus Watermark ChatGPT – Bersihkan Teks AI Gratis',
        description: 'Hapus watermark ChatGPT & AI dari teks dan dokumen. Deteksi dan hapus karakter tak terlihat lebar-nol dari Word, Pages & teks. Alat online gratis – tidak ada data yang disimpan.',
      },
    };

    const fallback = localizedFallbacks[locale] || localizedFallbacks.en;
    const pageTitle = title || pageConfig?.title || fallback.title;
    const pageDescription = description || pageConfig?.description || fallback.description;

    // Calculate alternate locales (all locales except the current one)
    const allLocales = ['en_US', 'de_DE', 'pt_BR', 'ms_MY', 'es_ES', 'fr_FR', 'ja_JP', 'id_ID'];
    const currentOgLocale = locale === 'ms-MY' ? 'ms_MY' : locale === 'pt-BR' ? 'pt_BR' : locale === 'de' ? 'de_DE' : locale === 'es' ? 'es_ES' : locale === 'fr' ? 'fr_FR' : locale === 'ja' ? 'ja_JP' : locale === 'id' ? 'id_ID' : 'en_US';
    const alternateLocales = allLocales.filter(l => l !== currentOgLocale);

    const ogLocaleContent = currentOgLocale;

    // Create DocumentFragment to batch all new elements
    const fragment = document.createDocumentFragment();
    const elementsToAdd: Element[] = [];

    // Prepare meta description
    let metaDescription = existingElements.metaDescription;
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      elementsToAdd.push(metaDescription);
    }
    metaDescription.setAttribute('content', pageDescription);

    // Prepare meta robots
    let metaRobots = existingElements.metaRobots;
    if (robotsDirective) {
      if (!metaRobots) {
        metaRobots = document.createElement('meta');
        metaRobots.setAttribute('name', 'robots');
        elementsToAdd.push(metaRobots);
      }
      metaRobots.setAttribute('content', robotsDirective);
    } else if (metaRobots) {
      metaRobots.remove();
    }

    // Prepare Open Graph title
    let ogTitle = existingElements.ogTitle;
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      elementsToAdd.push(ogTitle);
    }
    ogTitle.setAttribute('content', pageTitle);

    // Prepare Open Graph description
    let ogDescription = existingElements.ogDescription;
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      elementsToAdd.push(ogDescription);
    }
    ogDescription.setAttribute('content', pageDescription);

    // Prepare Twitter title
    let twitterTitle = existingElements.twitterTitle;
    if (!twitterTitle) {
      twitterTitle = document.createElement('meta');
      twitterTitle.setAttribute('name', 'twitter:title');
      elementsToAdd.push(twitterTitle);
    }
    twitterTitle.setAttribute('content', pageTitle);

    // Prepare Twitter description
    let twitterDescription = existingElements.twitterDescription;
    if (!twitterDescription) {
      twitterDescription = document.createElement('meta');
      twitterDescription.setAttribute('name', 'twitter:description');
      elementsToAdd.push(twitterDescription);
    }
    twitterDescription.setAttribute('content', pageDescription);

    // Prepare canonical link
    let canonicalLink = existingElements.canonicalLink;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      elementsToAdd.push(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

    // Prepare Open Graph locale
    let ogLocale = existingElements.ogLocale;
    if (!ogLocale) {
      ogLocale = document.createElement('meta');
      ogLocale.setAttribute('property', 'og:locale');
      elementsToAdd.push(ogLocale);
    }
    ogLocale.setAttribute('content', ogLocaleContent);

    // Prepare og:url
    let ogUrl = existingElements.ogUrl;
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      elementsToAdd.push(ogUrl);
    }
    ogUrl.setAttribute('content', canonicalUrl);

    // Prepare hreflang links
    hreflangLinks.forEach(({ lang, url }) => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = lang;
      link.href = url;
      elementsToAdd.push(link);
    });

    // x-default hreflang
    const defaultLink = document.createElement('link');
    defaultLink.rel = 'alternate';
    defaultLink.hreflang = 'x-default';
    defaultLink.href = `https://gpt-watermark-remover.com${normalized}`;
    elementsToAdd.push(defaultLink);

    // Prepare alternate locales
    alternateLocales.forEach(altLocale => {
      const ogLocaleAlternate = document.createElement('meta');
      ogLocaleAlternate.setAttribute('property', 'og:locale:alternate');
      ogLocaleAlternate.setAttribute('content', altLocale);
      elementsToAdd.push(ogLocaleAlternate);
    });

    // Update or create breadcrumb JSON-LD schema
    // First, remove any existing breadcrumb schema
    const existingBreadcrumbScript = document.querySelector('script[type="application/ld+json"][data-breadcrumb="true"]');

    // Generate new breadcrumb schema
    const breadcrumbSchema = generateBreadcrumbList(location.pathname, locale);

    if (breadcrumbSchema) {
      // Create or update breadcrumb script
      let breadcrumbScript = existingBreadcrumbScript as HTMLScriptElement | null;

      if (!breadcrumbScript) {
        breadcrumbScript = document.createElement('script');
        breadcrumbScript.type = 'application/ld+json';
        breadcrumbScript.setAttribute('data-breadcrumb', 'true');
        elementsToAdd.push(breadcrumbScript);
      }

      breadcrumbScript.textContent = breadcrumbsToJsonLd(breadcrumbSchema);
    } else if (existingBreadcrumbScript) {
      // Remove breadcrumb schema if we're on homepage or invalid page
      existingBreadcrumbScript.remove();
    }

    // BATCH DOM WRITES - Single reflow!
    // Update document title
    document.title = pageTitle;

    // Update lang attribute
    document.documentElement.lang = locale;

    // Remove old elements
    existingElements.existingHreflangs.forEach(link => link.remove());
    existingElements.existingAlternates.forEach(alt => alt.remove());

    // Add all new elements at once using DocumentFragment
    elementsToAdd.forEach(el => fragment.appendChild(el));
    head.appendChild(fragment);

  }, [location.pathname, locale, title, description]);

  return null; // This component doesn't render anything
};
